import { Grid } from '@mui/material';
import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { selectIsEligible, selectPartnerHasMemberCode } from 'selectors/partnerStatusSelector';
import { selectShownPage } from 'selectors/shownPageSelector';
import { Features } from 'reducers/partnerStatusReducer';
import { FeatureEligibility } from 'pco-product-service-internal-consumer';
import { cardStyle } from 'views/OrderNewProductOrService/OrderNewProductOrService';
import CardBodyComponent from '../common/card/CardBodyComponent';
import CardHeaderComponent from '../common/card/CardHeaderComponent';
import CardTooltipButton from '../common/card/CardTooltipButton';

interface JBMobileInsightsAccessProps {
  partnerId?: string;
}

const JBMobileInsightsAccess = ({ partnerId }: JBMobileInsightsAccessProps) => {
  const navigate = useNavigate();
  const hasMemberCode = useSelector(selectPartnerHasMemberCode);
  const shownPage = useSelector(selectShownPage);
  const isPartnerEligibleStore = useSelector(selectIsEligible) as
    | (FeatureEligibility[] & { message?: string; messages?: string[] })
    | undefined;

  const handleGenerateCode = () => {
    if (shownPage) {
      navigate(`/partner?partnerNumber=${partnerId}`);
    } else {
      navigate(`/partner/${partnerId}/new/memberCode`);
    }
  };

  const isEligibleForMemberZone = useMemo<FeatureEligibility | undefined>(() => {
    if (isPartnerEligibleStore?.message) return undefined;
    return isPartnerEligibleStore?.find((eligibility) => eligibility.feature === Features.MEMBERZONE) ?? undefined;
  }, [isPartnerEligibleStore]);

  const title = useMemo<React.ReactNode>(() => {
    if (isPartnerEligibleStore?.messages) {
      const messages = isPartnerEligibleStore.messages;
      return (
        <>
          <div>{partnerId ? 'Partner' : 'Business relation '} is missing following data:</div>
          {messages.map((message, index) => (
            <div key={index}>{message}</div>
          ))}
        </>
      );
    }
    return `${partnerId ? 'Partner' : 'Business relation'}  is not eligible for JB Mobile Insights`;
  }, [isPartnerEligibleStore, partnerId]);

  return (
    <Grid data-testid="JB-mobile-insights-access" item sx={cardStyle}>
      <CardHeaderComponent title={'JB mobile insights access'} />
      <CardBodyComponent content={'Request the access code to the e-lounge for a prospecting client.'} />
      <CardTooltipButton
        id={'jb-mobile-insights-access-button'}
        buttonText={'Generate prospect code'}
        isEligible={(hasMemberCode || isEligibleForMemberZone?.isEligible) ?? false}
        handleClick={handleGenerateCode}
        tooltipTitle={title}
      />
    </Grid>
  );
};

export default JBMobileInsightsAccess;
